import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { getInsuranceButtonText } from 'pages/avia/insurance-services/libs/getInsuranceButtonText.tsx'

import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import formPassengersStore from 'entities/ticket/models/form-passengers/form-passengers-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'
import { TYPE_TIMER } from 'entities/ticket/models/timer'
import timerStore from 'entities/ticket/models/timer/timer-store'
import InsuranceTariffPop from 'entities/ticket/ui/insurance-tariff-pop'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { TicketInsurancesApi } from 'shared/api/avia/insurance'
import { ShareKeys } from 'shared/constants'
import { parseAviaShareStorage } from 'shared/lib/avia-share-storage'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { priceShow } from 'shared/lib/priceShow'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { PageLoader } from 'shared/ui/page-loader'

import Pop, { BUY_TICKET } from '../detail-ticket/components/Pop'

import { InsuranceDetailSource } from './model/model.ts'
import s from './styles.module.scss'
import { InsuranceServicesList } from './сomponents/insurance-services-list'

const InsuranceServices = observer(() => {
  const { closeDialog, openDialog } = useDialogBottomSheet()

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const hasUser = Boolean(userStore.account)

  const selectedInsurances = formPassengersStore.selectedInsurances
  const noInsurances = formPassengersStore.ticketInsurances?.length === 0

  const ticket = listTicketsStore.getParentTicket()

  useEffect(() => {
    if (ticket) return

    const saved = userStore.getClientStateParse(ShareKeys.avia)
    const selection = parseAviaShareStorage(saved)

    if (selection) {
      listTicketsStore.setSelectedTicket(selection)
      void setSettingForApp({ key: ShareKeys.avia, value: '' })
    }
  }, [ticket, userStore.client_state])

  useEffect(() => {
    if (!ticket?.tid || !ticket?.uid || !ticket?.it_ref || !hasUser) {
      return
    }

    const loadInsurances = async () => {
      try {
        const { data: insurance } =
          await TicketInsurancesApi.getTicketInsurances({
            tid: ticket?.tid,
            uid: ticket?.uid,
            itref: ticket?.it_ref,
          })

        formPassengersStore.setInsuranceTariffCondition(
          insurance?.insurance_contidion,
        )

        if ((formPassengersStore?.ticketInsurances?.length || 0) === 0) {
          formPassengersStore.setTicketInsurances(insurance.data)
        }
      } catch (error) {
        const errorMessage = getApiErrorMessage(error)

        formPassengersStore.setTicketInsurances([])
        formPassengersStore.setInsuranceErrorMessage(errorMessage)
      }
    }

    void loadInsurances()
  }, [ticket?.tid, ticket?.uid, ticket?.it_ref, hasUser])

  if (!ticket) return <PageLoader />

  const onContinue = (type: BUY_TICKET) => {
    findTicketFormStore?.onBuyForMySelf(type === BUY_TICKET.FOR_MY_SELF)
    closeDialog()
    navigate(RoutingPaths.avia.purchase.path())

    timerStore.onResetTimer(TYPE_TIMER.FORM_PURCHASE)
  }

  const onBuyTicket = () => {
    if (formPassengersStore?.formPassengersStore) {
      onContinue(
        findTicketFormStore.formFindTicket.forMySelf
          ? BUY_TICKET.FOR_MY_SELF
          : BUY_TICKET.FOR_OTHER,
      )

      return
    }

    openDialog({
      child: <Pop onContinue={onContinue} />,
      isDialog: false,
    })
  }

  const onShowTariff = () => {
    openDialog({
      child: (
        <InsuranceTariffPop
          content={formPassengersStore.insuranceTariffCondition}
        />
      ),
    })
  }

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    const detailSource = location.state?.detailSource

    if (detailSource === InsuranceDetailSource.favorite) {
      navigate(RoutingPaths.avia.favorites.path())
      return
    }

    navigate(
      RoutingPaths.avia.detailTicket.path(
        String(listTicketsStore.activeTicketIndex ?? ''),
      ),
    )
  }

  return (
    <div className="container">
      <HeaderGoBack
        title={t('insurance_info.insurance_service')}
        onGoBack={handleGoBack}
        buttonWhite
      />

      <div className={s.content}>
        <InsuranceServicesList />

        {!noInsurances && (
          <p
            className={clsx('medium-14', s.insuranceRules)}
            onClick={onShowTariff}
          >
            {t('insurance_info.condition_insurance')}
          </p>
        )}

        <div className={s.insuranceButtons}>
          <Button onClick={onBuyTicket}>
            <p className="semi-bold-16">
              {getInsuranceButtonText({
                noInsurances,
                hasSelected: Boolean(selectedInsurances?.length),
                priceText: priceShow(
                  formPassengersStore.calculateInsurancePrice(),
                ),
              })}
            </p>
          </Button>
        </div>
      </div>
    </div>
  )
})

export default InsuranceServices
